import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Search, Menu, X, ChevronLeft, ChevronRight, Sun, Moon, DollarSign, Shield } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import BuscaProcessual from './components/BuscaProcessual';
import DetalhesProcesso from './components/DetalhesProcesso';
import Consumo from './components/Consumo';
import Gestao from './components/Gestao';
import Login from './components/Login';

function AuthenticatedApp() {
  const { currentUser, logout, darkMode, toggleDarkMode } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getPageTitle = () => {
    switch(location.pathname) {
      case '/busca-processual': return 'Processos';
      case '/consumo': return 'Consumo';
      case '/gestao': return 'Gestão';
      default: return 'Juridic AI';
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  return (
    <div className={`flex h-screen overflow-hidden ${darkMode ? 'bg-neutral-950' : 'bg-gray-50'}`}>
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 flex flex-col
        transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-72'}
        w-72
        ${darkMode ? 'bg-neutral-900 border-r border-neutral-800' : 'bg-white border-r border-gray-200'}
        shadow-xl
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} h-16 px-6 ${darkMode ? 'bg-neutral-950/50 backdrop-blur-sm border-b border-neutral-800' : 'bg-gray-50/50 backdrop-blur-sm'}`}>
            <div className={`flex items-center min-w-0 ${sidebarCollapsed ? '' : 'flex-1'} overflow-hidden`}>
              {sidebarCollapsed ? (
                <img
                  src={darkMode ? '/logo-symbol-branco.svg' : '/logo-symbol.svg'}
                  alt="Juridic AI"
                  className="w-9 h-9 transition-transform hover:scale-110"
                />
              ) : (
                <img
                  src={darkMode ? '/logo-wordmark-branco.svg' : '/logo-wordmark.svg'}
                  alt="Juridic AI"
                  className="h-8 w-auto max-w-[90%] object-contain transition-transform hover:scale-105"
                />
              )}
            </div>
            {!sidebarCollapsed && (
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden text-neutral-500 hover:text-neutral-300"
                >
                  <X className="w-6 h-6" />
                </button>
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className={`hidden lg:flex ${darkMode ? 'text-neutral-400 hover:text-neutral-200' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              </div>
            )}
            {sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={`hidden lg:flex absolute right-2 top-5 ${darkMode ? 'text-neutral-400 hover:text-neutral-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {[
              { path: '/busca-processual', label: 'Consulta', icon: <Search className="w-5 h-5 flex-shrink-0" /> },
              { path: '/consumo', label: 'Billing', icon: <DollarSign className="w-5 h-5 flex-shrink-0" /> },
              ...(currentUser?.role === 'admin' ? [{ path: '/gestao', label: 'Admin', icon: <Shield className="w-5 h-5 flex-shrink-0" /> }] : []),
            ].map(({ path, label, icon }) => {
              const active = location.pathname === path;
              return (
                <button
                  key={path}
                  onClick={() => handleNavigation(path)}
                  className={`
                    flex items-center w-full px-4 py-3.5 rounded-xl font-medium transition-all relative group
                    ${sidebarCollapsed ? 'justify-center' : 'justify-start'}
                    ${active
                      ? (darkMode
                        ? 'bg-primary-600 text-white shadow-lg shadow-primary-900/50'
                        : 'bg-primary-500 text-white shadow-lg shadow-primary-400/30')
                      : (darkMode
                        ? 'text-neutral-300 hover:bg-neutral-800/50 hover:text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900')
                    }
                  `}
                  title={sidebarCollapsed ? label : ''}
                >
                  {active && !sidebarCollapsed && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                  )}
                  {icon}
                  {!sidebarCollapsed && <span className="ml-3">{label}</span>}
                </button>
              );
            })}
          </nav>

          {/* Profile Menu */}
          <div className={`p-4 ${darkMode ? 'bg-neutral-950/30 border-t border-neutral-800' : 'bg-gray-50/50'} relative`}>
            <button
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              className={`
                flex items-center w-full px-3 py-3 rounded-xl font-medium transition-all
                ${sidebarCollapsed ? 'justify-center' : 'justify-between'}
                ${darkMode ? 'text-neutral-300 hover:bg-neutral-800/70' : 'text-gray-600 hover:bg-white hover:shadow-md'}
              `}
              title={sidebarCollapsed ? currentUser?.name : ''}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  darkMode ? 'bg-primary-900/30 text-primary-400' : 'bg-primary-100 text-primary-600'
                }`}>
                  {currentUser?.name?.charAt(0).toUpperCase()}
                </div>
                {!sidebarCollapsed && (
                  <div className="min-w-0 flex-1 text-left">
                    <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {currentUser?.name}
                    </p>
                    <p className={`text-xs truncate ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                      {currentUser?.username}
                    </p>
                  </div>
                )}
              </div>

              {!sidebarCollapsed && (
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>

            {/* Dropdown Menu */}
            {profileMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={() => setProfileMenuOpen(false)}
                />
                <div className={`
                  absolute ${sidebarCollapsed ? 'left-full ml-2 bottom-3' : 'left-3 right-3 bottom-full mb-2'}
                  z-40 rounded-xl shadow-xl border
                  ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'}
                `}>
                  <div className="p-2">
                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        logout();
                      }}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors
                        ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'}
                      `}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>Sair</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Overlay para mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className={`${darkMode ? 'bg-neutral-950/80 backdrop-blur-sm border-neutral-800' : 'bg-white border-gray-200'} border-b h-16 flex items-center px-4 lg:px-8 justify-between`}>
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`lg:hidden p-2 rounded-lg transition-colors mr-4 ${
                darkMode ? 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {getPageTitle()}
            </h1>
          </div>

          <div className="flex items-center space-x-4">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? 'bg-neutral-800 text-yellow-400 hover:bg-neutral-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={darkMode ? 'Modo claro' : 'Modo escuro'}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <Routes>
            <Route path="/" element={<Navigate to="/busca-processual" replace />} />
            <Route path="/busca-processual" element={<BuscaProcessual />} />
            <Route path="/detalhes-processo" element={<DetalhesProcesso />} />
            <Route path="/consumo" element={<Consumo />} />
            <Route path="/gestao" element={<Gestao />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return <Login />;
  }

  return <AuthenticatedApp />;
}
